// React
import { useState } from "react";

// React redux
import { useDispatch, useSelector } from "react-redux";

// @mui material components
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Breadcrumbs,
  CircularProgress,
  FormHelperText,
  Tooltip,
  IconButton,
  CardMedia,
} from "@mui/material";
import { Close, NavigateNext, PhotoCamera } from "@mui/icons-material";

// Material Dashboard 2 React example components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Service
import { update } from "services/tag/tags.services";
import removeImage from "services/image/images.services";

// Constants
import { validForm, validField } from "constants/utils/tag/validator";
import { errorMessage } from "constants/utils/tag/errors.messages";
import { formData } from "constants/utils/tag/formdata";
import { SERVER_ERROR } from "constants/messages/error";
import { SERVER_URL } from "constants/server/api";

function ModalEdit({ tag, finished }) {
  const allCourses = useSelector((state) => state.course.allCourses);
  const [fields, setFields] = useState({
    id: { value: tag.id, error: "" },
    wording: { value: tag.wording, error: "" },
    imageFile: { value: null, error: "" },
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const dispatchApi = useDispatch();

  const handleChange = (e) => {
    e.preventDefault();
    const { name, value, files } = e.target;
    if (error) setError("");
    setFields((f) => ({
      ...f,
      [name]: {
        value: (files && files[0]) || value,
        error: validField(name, value) ? "" : errorMessage[name],
      },
    }));
  };

  const handleBlur = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    if (value.length === 0) setFields((f) => ({ ...f, [name]: { ...f[name], error: "" } }));
  };

  const handleUpdate = async (e) => {
    let passed = true;
    e.preventDefault();
    if (!validForm(fields)) {
      Object.entries(fields).forEach(([name, { value }]) =>
        setFields((f) => ({
          ...f,
          [name]: { value, error: validField(name, value) ? "" : errorMessage[name] },
        }))
      );
      return;
    }
    if (error) setError("");
    Object.keys(fields).forEach((name) =>
      setFields((f) => ({
        ...f,
        [name]: {
          ...f[name],
          error: "",
        },
      }))
    );
    setLoading(true);
    if (tag.image && fields.imageFile.remove) {
      await removeImage(tag.image.id);
    }
    update(formData(fields))
      .then(({ data }) => {
        const courses = allCourses.slice();
        courses.forEach((course, i) => {
          courses[i].tags = course.tags.map((t) =>
            t.id === tag.id ? { ...t, updatedAt: data.updatedAt, wording: data.wording } : t
          );
        });
        dispatchApi({ type: "UPDATED_TAG", data });
        dispatchApi({ type: "EMPTY_COURSE" });
        dispatchApi({ type: "FETCHED_COURSE", data: courses });
      })
      .catch((err) => {
        passed = false;
        if (err.request.status) {
          const { violations } = err.response.data;
          violations.forEach((violation) =>
            setFields((f) => ({
              ...f,
              [violation.propertyPath]: { ...f[violation.propertyPath], error: violation.message },
            }))
          );
        } else setError(SERVER_ERROR);
      })
      .finally(() => {
        setLoading(false);
        if (passed) finished();
      });
  };

  return (
    <Dialog open>
      <DialogTitle>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb">
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Catégorie
          </MDTypography>
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Modification
          </MDTypography>
        </Breadcrumbs>
      </DialogTitle>
      <MDBox component="form" role="form" onSubmit={handleUpdate} noValidate autoComplete="off">
        <DialogContent>
          <MDBox mb={1}>
            <MDInput
              required
              error={Boolean(fields.wording.error)}
              type="text"
              label="Libellé"
              name="wording"
              value={fields.wording.value}
              disabled={loading}
              placeholder="Saisir le libellé"
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
            />
            {fields.wording.error && <FormHelperText error>{fields.wording.error}</FormHelperText>}
          </MDBox>
          <MDBox mb={2}>
            <label htmlFor="icon-button-file">
              <input
                name="imageFile"
                accept="image/*"
                id="icon-button-file"
                type="file"
                style={{ display: "none" }}
                onChange={handleChange}
              />
              <IconButton color="info" aria-label="upload picture" component="span">
                <PhotoCamera />
                <MDTypography variant="button" pl={2} fontWeight="bold">
                  Image
                </MDTypography>
              </IconButton>
            </label>
          </MDBox>
          {((Boolean(tag.image) && !fields.imageFile.remove) ||
            Boolean(fields.imageFile.value)) && (
            <>
              <Tooltip
                title="Enlever l'image"
                placement="right"
                sx={{ position: "absolute" }}
                onClick={() =>
                  setFields((f) => ({
                    ...f,
                    imageFile: {
                      value: null,
                      remove: true,
                      error: "",
                    },
                  }))
                }
              >
                <IconButton>
                  <Close />
                </IconButton>
              </Tooltip>
              <CardMedia
                component="img"
                height="140"
                image={
                  fields.imageFile.value
                    ? URL.createObjectURL(fields.imageFile.value)
                    : `${SERVER_URL}${tag.image.imageUrl}`
                }
                alt={tag.wording}
              />
            </>
          )}
          <MDBox mt={2} mb={1}>
            {error && <FormHelperText error>{error}</FormHelperText>}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={finished} disabled={loading} variant="text" color="info" size="small">
            Retour
          </MDButton>
          <MDButton type="submit" disabled={loading} variant="text" color="info" size="small">
            Valider
            {loading && (
              <CircularProgress size="1.5em" color="info" sx={{ position: "absolute" }} />
            )}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

ModalEdit.propTypes = {
  tag: PropTypes.oneOfType([PropTypes.object]).isRequired,
  finished: PropTypes.func.isRequired,
};

export default ModalEdit;
